import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  KnowledgeConflictDTO,
  KnowledgeDocDTO,
  ProjectDetailDTO,
} from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  EmptyState,
  Field,
  Panel,
  inputClass,
} from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import {
  canContribute,
  canManage,
  useConflicts,
  useKnowledgeTree,
  useProjectRole,
  useUploads,
} from "../project-data";

const textareaClass = `${inputClass} min-h-[220px] resize-y font-mono leading-relaxed`;

/** KNOWLEDGE dle /Design: uploads · structured tree · detail s provenancí. */
export function KnowledgeTab({ project }: { project: ProjectDetailDTO }) {
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const role = useProjectRole(project);

  return (
    // p5a: tři samostatné panely (330 / 330 / zbytek), ne jedna karta s příčkami
    <div className="mt-4 grid grid-cols-1 gap-4 xl:min-h-[520px] xl:grid-cols-[320px_320px_1fr]">
      <UploadsColumn project={project} contributor={canContribute(role)} />
      <TreeColumn
        project={project}
        selectedDocId={selectedDocId}
        onSelect={(id) => {
          setCreating(false);
          setSelectedDocId(id);
        }}
        onCreate={() => setCreating(true)}
        contributor={canContribute(role)}
      />
      <Panel className="overflow-auto px-4 py-4 xl:px-6">
        {creating ? (
          <NewDocForm
            project={project}
            onDone={(id) => {
              setCreating(false);
              setSelectedDocId(id);
            }}
            onCancel={() => setCreating(false)}
          />
        ) : selectedDocId ? (
          <DocDetail
            key={selectedDocId}
            project={project}
            docId={selectedDocId}
          />
        ) : (
          <EmptyState title={cs.knowledge.emptyDoc} />
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

function UploadsColumn({
  project,
  contributor,
}: {
  project: ProjectDetailDTO;
  contributor: boolean;
}) {
  const uploads = useUploads(project.id);
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadFile(project.id, file),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({
        queryKey: ["section", project.id, "uploads"],
      });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  const reprocessMutation = useMutation({
    mutationFn: (id: string) => api.reprocessUpload(id),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({
        queryKey: ["section", project.id, "uploads"],
      });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  const handleFiles = (files: FileList | null) => {
    if (!files?.length || !contributor) return;
    for (const file of Array.from(files)) uploadMutation.mutate(file);
  };

  return (
    <Panel className="overflow-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="af-label" style={{ color: "var(--accent)" }}>
          {cs.knowledge.uploads}
        </span>
        {contributor ? (
          <Btn
            variant="secondary"
            size="sm"
            className="tracking-[0.12em]"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-button"
          >
            {cs.knowledge.upload}
          </Btn>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          hidden
          multiple
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="upload-input"
        />
      </div>
      <div
        className="mt-1 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.knowledge.uploadsNote}
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {(uploads.data?.uploads ?? []).map((u) => (
          <a
            key={u.id}
            href={api.downloadUploadUrl(u.id)}
            className="block px-3 py-2"
            style={{
              borderRadius: "var(--radius-box)",
              border: "1px solid var(--border-subtle)",
            }}
            data-testid="upload-card"
          >
            <div
              className="break-words text-[12px]"
              style={{ color: "var(--text-primary)" }}
            >
              📄 {u.filename}
            </div>
            <div
              className="mt-0.5 text-[11px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              {formatBytes(u.sizeBytes)} · {relativeTime(u.createdAt)}
              {u.note ? ` · ${u.note}` : ""}
            </div>
            <div
              className="mt-0.5 text-[11px]"
              style={{
                color:
                  u.status === "PROCESSED"
                    ? "var(--positive)"
                    : "var(--warning)",
              }}
            >
              {u.status === "PROCESSED"
                ? `✓ zpracováno do knowledge${u.taskRefs.length ? ` → ${u.taskRefs.map((t) => `#${t.number} ${t.title}`).join(" · ")}` : ""}`
                : "čeká na zpracování"}
            </div>
            {contributor && u.status === "PROCESSED" ? (
              <Btn
                size="sm"
                className="mt-1.5"
                disabled={reprocessMutation.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (
                    window.confirm(cs.knowledge.reprocessConfirm(u.filename))
                  ) {
                    reprocessMutation.mutate(u.id);
                  }
                }}
                data-testid="upload-reprocess"
              >
                {cs.knowledge.reprocess}
              </Btn>
            ) : null}
          </a>
        ))}
        {(uploads.data?.uploads ?? []).length === 0 ? (
          <div
            className="text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.knowledge.noUploads}
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}

      {contributor ? (
        <div
          className="mt-3 px-4 py-4 text-center text-[11px]"
          style={{
            border: `1px dashed ${dragOver ? "var(--accent)" : "color-mix(in srgb, var(--accent) 20%, transparent)"}`,
            color: "var(--text-tertiary)",
            background: dragOver
              ? "color-mix(in srgb, var(--accent) 4%, transparent)"
              : "transparent",
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          {cs.knowledge.dropzone}
          <br />
          {cs.knowledge.dropzoneNote}
        </div>
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function TreeColumn({
  project,
  selectedDocId,
  onSelect,
  onCreate,
  contributor,
}: {
  project: ProjectDetailDTO;
  selectedDocId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  contributor: boolean;
}) {
  const tree = useKnowledgeTree(project.id);
  const folders = tree.data?.folders ?? [];
  const hasDocs = folders.some((f) => f.docs.length > 0);

  return (
    <Panel className="overflow-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="af-label" style={{ color: "var(--accent)" }}>
          {cs.knowledge.structured}
        </span>
        <div className="flex items-center gap-2">
          {hasDocs ? (
            <a
              href={api.knowledgeExportUrl(project.id)}
              className="text-[11px] tracking-[0.1em]"
              style={{ color: "var(--text-secondary)" }}
              title="Export všech aktivních dokumentů jako Markdown ZIP"
            >
              {cs.knowledge.exportMd}
            </a>
          ) : null}
          {contributor ? (
            <Btn
              variant="secondary"
              size="sm"
              className="tracking-[0.12em]"
              onClick={onCreate}
              data-testid="knowledge-new-doc"
            >
              {cs.knowledge.newDoc}
            </Btn>
          ) : null}
        </div>
      </div>
      <div
        className="mt-1 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {cs.knowledge.structuredNote}
      </div>

      <div className="mt-3 flex flex-col gap-3">
        {folders.length === 0 ? (
          <div
            className="text-[11px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.knowledge.emptyTree}
          </div>
        ) : (
          folders.map((f) => (
            <div key={f.folder}>
              <div
                className="mb-1 text-[11px] tracking-[0.12em]"
                style={{ color: "var(--text-secondary)" }}
              >
                ▸ {f.folder}/
              </div>
              {f.docs.map((doc) => {
                const active = doc.id === selectedDocId;
                return (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => onSelect(doc.id)}
                    className="flex w-full cursor-pointer items-center justify-between px-2 py-1.5 pl-4 text-left text-[11px]"
                    style={{
                      background: active
                        ? "color-mix(in srgb, var(--accent) 6%, transparent)"
                        : "transparent",
                      borderLeft: `2px solid ${
                        doc.hasOpenConflict
                          ? "var(--warning)"
                          : active
                            ? "var(--accent)"
                            : "transparent"
                      }`,
                    }}
                    data-testid="knowledge-doc-row"
                  >
                    <span
                      className="min-w-0 break-words"
                      style={{
                        color:
                          doc.status !== "ACTIVE"
                            ? "var(--text-tertiary)"
                            : active
                              ? "var(--text-primary)"
                              : "var(--text-secondary)",
                        textDecoration:
                          doc.status === "SUPERSEDED"
                            ? "line-through"
                            : undefined,
                      }}
                    >
                      {doc.hasOpenConflict ? "⚠ " : ""}
                      {doc.slug}.md
                      {/* tečka = dokument bez spouštěče, workerovi neviditelný */}
                      {doc.status === "ACTIVE" && !doc.indexed ? (
                        <span
                          style={{ color: "var(--warning)" }}
                          title={cs.knowledge.noTriggerDetail}
                          data-testid="knowledge-doc-no-trigger"
                        >
                          {" ●"}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="flex-none text-[11px]"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {relativeTime(doc.updatedAt)}
                    </span>
                  </button>
                );
              })}
              {f.docs.length === 0 ? (
                <div
                  className="pl-4 text-[11px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  prázdné
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function DocDetail({
  project,
  docId,
}: {
  project: ProjectDetailDTO;
  docId: string;
}) {
  const queryClient = useQueryClient();
  const role = useProjectRole(project);
  const doc = useQuery({
    queryKey: ["knowledge-doc", docId],
    queryFn: () => api.knowledgeDoc(docId),
  });
  const conflicts = useConflicts(project.id);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);

  if (!doc.data) return null;
  const d = doc.data.doc;
  const openConflict = (conflicts.data?.conflicts ?? []).find(
    (c) => c.docId === docId && c.status === "OPEN",
  );
  const lastResolved = (conflicts.data?.conflicts ?? []).find(
    (c) => c.docId === docId && c.status === "RESOLVED",
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["knowledge-doc", docId] });
    void queryClient.invalidateQueries({
      queryKey: ["section", project.id, "knowledge"],
    });
    void queryClient.invalidateQueries({
      queryKey: ["section", project.id, "conflicts"],
    });
  };

  return (
    <div data-testid="knowledge-doc-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="af-display min-w-0 break-words text-[15px] font-semibold"
          style={{ color: "var(--text-primary)" }}
        >
          {d.title}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* dokument bez spouštěče vypadá založený, ale worker ho nikdy
              nedostane — proto to musí být vidět hned u stavu */}
          {d.status === "ACTIVE" && !d.indexed ? (
            <Chip tone="amber">{cs.knowledge.noTrigger}</Chip>
          ) : null}
          {d.status === "ACTIVE" ? (
            <Chip tone="green">{cs.knowledge.currentTruth}</Chip>
          ) : (
            <Chip tone="dim">
              {d.status === "SUPERSEDED"
                ? cs.knowledge.superseded
                : cs.knowledge.archived}
            </Chip>
          )}
          <Btn
            variant={showHistory ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setShowHistory((v) => !v)}
            data-testid="knowledge-history-toggle"
          >
            {cs.knowledge.history(d.revisionCount)}
          </Btn>
          {canContribute(role) && d.status === "ACTIVE" && !editing ? (
            <Btn
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="knowledge-edit"
            >
              {cs.knowledge.edit}
            </Btn>
          ) : null}
        </div>
      </div>

      <div
        className="mt-1.5 text-[11px] leading-relaxed"
        style={{
          color: d.indexed ? "var(--text-tertiary)" : "var(--warning)",
        }}
        data-testid="knowledge-read-when"
      >
        {d.indexed ? (
          <>
            <span style={{ color: "var(--accent)" }}>
              {cs.knowledge.readWhenLabel}
            </span>{" "}
            {d.readWhen}
          </>
        ) : (
          cs.knowledge.noTriggerDetail
        )}
      </div>

      {openConflict ? (
        <ConflictPanel
          conflict={openConflict}
          doc={d}
          manager={canManage(role)}
          onResolved={invalidate}
        />
      ) : lastResolved?.chosen ? (
        <div
          className="mt-3 px-4 py-2.5 text-[12px]"
          style={{
            borderRadius: "var(--radius-box)",
            border:
              "1px solid color-mix(in srgb, var(--positive) 30%, transparent)",
            background: "color-mix(in srgb, var(--positive) 3%, transparent)",
            color: "var(--text-secondary)",
          }}
        >
          ✓{" "}
          <span style={{ color: "var(--positive)" }}>
            {cs.knowledge.resolvedNote(
              lastResolved.chosen === "A"
                ? lastResolved.optionA.label
                : lastResolved.chosen === "B"
                  ? lastResolved.optionB.label
                  : (lastResolved.resolutionNote ?? "vlastní řešení"),
            )}
          </span>
        </div>
      ) : null}

      {editing ? (
        <EditForm
          docId={docId}
          initialContent={d.content}
          initialReadWhen={d.readWhen}
          onDone={() => {
            setEditing(false);
            invalidate();
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div
          className="mt-3 whitespace-pre-wrap px-5 py-4 text-[12px] leading-[1.85]"
          style={{
            borderRadius: "var(--radius-box)",
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-overlay)",
            color: "var(--text-primary)",
          }}
          data-testid="knowledge-content"
        >
          {d.content}
        </div>
      )}

      {showHistory ? (
        <Panel className="mt-3 p-4">
          <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
            {cs.knowledge.revisions}
          </div>
          {d.revisions.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-0.5 py-1.5 text-[11px] md:flex-row md:gap-3"
              style={{ borderTop: "1px solid var(--border-subtle)" }}
            >
              <span className="flex gap-3 md:contents">
                <span
                  className="w-8 shrink-0"
                  style={{ color: "var(--accent)" }}
                >
                  r{r.rev}
                </span>
                <span
                  className="w-24 shrink-0"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  {relativeTime(r.createdAt)}
                </span>
                <span
                  className="w-28 shrink-0"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {r.actorLabel}
                </span>
              </span>
              <span
                className="min-w-0 break-words"
                style={{ color: "var(--text-secondary)" }}
              >
                {r.changeSummary}
                <span style={{ color: "var(--text-tertiary)" }}>
                  {" "}
                  · {sourceLabel(r.sourceType, r.sourceUploadName, r.sourceRef)}
                </span>
              </span>
            </div>
          ))}
        </Panel>
      ) : null}

      <ProvenancePanel doc={d} />
    </div>
  );
}

function sourceLabel(
  sourceType: string,
  uploadName: string | null,
  sourceRef: string | null,
): string {
  if (sourceType === "UPLOAD") return `upload: ${uploadName ?? "?"}`;
  if (sourceType === "DECISION") return `rozhodnutí: ${sourceRef ?? "?"}`;
  if (sourceType === "AI") return `AI${sourceRef ? ` (${sourceRef})` : ""}`;
  if (sourceType === "SYSTEM") return "systém";
  return "člověk";
}

function ProvenancePanel({ doc }: { doc: KnowledgeDocDTO }) {
  const first = doc.revisions[doc.revisions.length - 1];
  const last = doc.revisions[0];
  const rows: { k: string; v: string; tone?: string }[] = [];
  if (first) {
    rows.push({
      k: cs.knowledge.created,
      v: `${relativeTime(first.createdAt)} · ${first.actorLabel} · ${sourceLabel(first.sourceType, first.sourceUploadName, first.sourceRef)}`,
    });
  }
  if (last) {
    rows.push({
      k: cs.knowledge.lastChange,
      v: `${relativeTime(last.createdAt)} · ${last.actorLabel} · ${sourceLabel(last.sourceType, last.sourceUploadName, last.sourceRef)}`,
    });
    rows.push({ k: cs.knowledge.why, v: last.changeSummary });
  }
  rows.push({
    k: cs.knowledge.revisions,
    v: `${doc.revisionCount}× změněno, plná historie výše`,
  });

  return (
    <div
      className="mt-3 px-4 py-3"
      style={{
        borderRadius: "var(--radius-box)",
        border: "1px solid color-mix(in srgb, var(--accent) 14%, transparent)",
      }}
      data-testid="provenance"
    >
      <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
        {cs.knowledge.provenance}
      </div>
      {rows.map((r) => (
        <div key={r.k} className="mb-1.5 flex gap-3 text-[11px]">
          <span className="w-28 flex-none" style={{ color: "var(--accent)" }}>
            {r.k}
          </span>
          <span
            className="min-w-0 break-words"
            style={{ color: "var(--text-primary)" }}
          >
            {r.v}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ConflictPanel({
  conflict,
  doc,
  manager,
  onResolved,
}: {
  conflict: KnowledgeConflictDTO;
  doc: KnowledgeDocDTO;
  manager: boolean;
  onResolved: () => void;
}) {
  const [choice, setChoice] = useState<"A" | "B" | null>(null);
  const [newContent, setNewContent] = useState(doc.content);
  const [error, setError] = useState<string | null>(null);

  const resolveMutation = useMutation({
    mutationFn: () =>
      api.resolveConflict(conflict.id, {
        choice: choice!,
        newContent: newContent !== doc.content ? newContent : undefined,
      }),
    onSuccess: onResolved,
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <div
      className="mt-3 px-4 py-4 sm:px-5"
      style={{
        borderRadius: "var(--radius-box)",
        border: "1px solid color-mix(in srgb, var(--warning) 45%, transparent)",
        background: "color-mix(in srgb, var(--warning) 4%, transparent)",
      }}
      data-testid="conflict-panel"
    >
      <div className="flex justify-between text-[11px] tracking-[0.18em]">
        <span style={{ color: "var(--warning)" }}>
          {cs.knowledge.conflictTitle}
        </span>
        <span style={{ color: "var(--text-tertiary)" }}>
          {relativeTime(conflict.createdAt)}
        </span>
      </div>
      <p
        className="mt-2 text-[12px] leading-relaxed"
        style={{ color: "var(--text-primary)" }}
      >
        {conflict.description}
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ConflictOption
          heading={cs.knowledge.conflictA}
          option={conflict.optionA}
          recommended={conflict.recommended === "A"}
          selected={choice === "A"}
          onPick={manager ? () => setChoice("A") : undefined}
        />
        <ConflictOption
          heading={cs.knowledge.conflictB}
          option={conflict.optionB}
          recommended={conflict.recommended === "B"}
          selected={choice === "B"}
          onPick={manager ? () => setChoice("B") : undefined}
        />
      </div>

      {manager && choice ? (
        <div className="mt-3">
          <Field label={cs.knowledge.newContentLabel}>
            <textarea
              className={textareaClass}
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              data-testid="conflict-new-content"
            />
          </Field>
          {error ? (
            <div
              className="mt-1 text-[11px]"
              style={{ color: "var(--critical)" }}
            >
              ⚠ {error}
            </div>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <Btn
              variant="primary"
              disabled={resolveMutation.isPending}
              onClick={() => resolveMutation.mutate()}
              data-testid="conflict-confirm"
            >
              {cs.knowledge.confirmResolve} (
              {choice === "A" ? conflict.optionA.label : conflict.optionB.label}
              )
            </Btn>
            <Btn onClick={() => setChoice(null)}>{cs.common.cancel}</Btn>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Btn
            disabled={!manager}
            onClick={() => setChoice("A")}
            title={manager ? undefined : "Rozhodnutí vyžaduje roli manažera"}
            data-testid="conflict-pick-a"
          >
            {cs.knowledge.pickA}
          </Btn>
          <Btn
            variant="primary"
            disabled={!manager}
            onClick={() => setChoice("B")}
            data-testid="conflict-pick-b"
          >
            {cs.knowledge.pickB}
          </Btn>
          <Btn disabled title="Jarvis přijde ve Fázi 8">
            {cs.knowledge.discussJarvis}
          </Btn>
        </div>
      )}
    </div>
  );
}

function ConflictOption({
  heading,
  option,
  recommended,
  selected,
  onPick,
}: {
  heading: string;
  option: { label: string; detail: string | null; sourceRef: string | null };
  recommended: boolean;
  selected: boolean;
  onPick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!onPick}
      onClick={onPick}
      className="px-3 py-2.5 text-left"
      style={{
        borderRadius: "var(--radius-box)",
        border: `1px solid ${
          selected
            ? "var(--accent)"
            : recommended
              ? "color-mix(in srgb, var(--positive) 30%, transparent)"
              : "color-mix(in srgb, var(--accent) 15%, transparent)"
        }`,
        background: recommended
          ? "color-mix(in srgb, var(--positive) 2%, transparent)"
          : "transparent",
        cursor: onPick ? "pointer" : "default",
      }}
    >
      <div
        className="text-[11px] tracking-[0.14em]"
        style={{
          color: recommended ? "var(--positive)" : "var(--text-tertiary)",
        }}
      >
        {heading} {recommended ? cs.knowledge.conflictRecommended : ""}
      </div>
      <div
        className="mt-1 text-[11px]"
        style={{ color: "var(--text-primary)" }}
      >
        {option.label}
      </div>
      <div
        className="mt-0.5 text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {option.detail ? `${option.detail} · ` : ""}
        {option.sourceRef ? `zdroj: ${option.sourceRef}` : ""}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------

function EditForm({
  docId,
  initialContent,
  initialReadWhen,
  onDone,
  onCancel,
}: {
  docId: string;
  initialContent: string;
  initialReadWhen: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [content, setContent] = useState(initialContent);
  const [readWhen, setReadWhen] = useState(initialReadWhen);
  const [changeSummary, setChangeSummary] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.updateKnowledgeDoc(docId, {
        content,
        readWhen,
        changeSummary,
        sourceType: "HUMAN",
      }),
    onSuccess: onDone,
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <div className="mt-3 flex flex-col gap-3" data-testid="knowledge-edit-form">
      <textarea
        className={textareaClass}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        data-testid="knowledge-edit-content"
      />
      <Field label={cs.knowledge.readWhen}>
        <input
          className={inputClass}
          value={readWhen}
          onChange={(e) => setReadWhen(e.target.value)}
          placeholder={cs.knowledge.readWhenPlaceholder}
          data-testid="knowledge-edit-read-when"
        />
      </Field>
      <div
        className="-mt-2 text-[11px]"
        style={{
          color: readWhen.trim() ? "var(--text-tertiary)" : "var(--warning)",
        }}
      >
        {cs.knowledge.readWhenNote}
      </div>
      <Field label={cs.knowledge.changeSummary}>
        <input
          className={inputClass}
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          placeholder="např. majitel změnil zálohu na 3 000 Kč"
          data-testid="knowledge-edit-summary"
        />
      </Field>
      {error ? (
        <div className="text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Btn onClick={onCancel}>{cs.common.cancel}</Btn>
        <Btn
          variant="primary"
          disabled={changeSummary.trim().length < 3 || mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="knowledge-edit-save"
        >
          {cs.common.save}
        </Btn>
      </div>
    </div>
  );
}

function NewDocForm({
  project,
  onDone,
  onCancel,
}: {
  project: ProjectDetailDTO;
  onDone: (docId: string) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const tree = useKnowledgeTree(project.id);
  const folders = (tree.data?.folders ?? []).map((f) => f.folder);
  const [folder, setFolder] = useState(folders[0] ?? "Product");
  const [customFolder, setCustomFolder] = useState("");
  const [title, setTitle] = useState("");
  const [readWhen, setReadWhen] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createKnowledgeDoc(project.id, {
        folder: customFolder.trim() || folder,
        title,
        readWhen,
        content,
        changeSummary: "Vytvořeno ručně",
        sourceType: "HUMAN",
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["section", project.id, "knowledge"],
      });
      onDone(data.doc.id);
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  return (
    <div className="max-w-xl" data-testid="knowledge-new-form">
      <div className="af-card-title" style={{ color: "var(--text-primary)" }}>
        Nový dokument
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={cs.knowledge.folder}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
          >
            {[
              ...new Set([...folders, "Product", "Business", "Engineering"]),
            ].map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </Field>
        <Field label={cs.fields.newFolder}>
          <input
            className={inputClass}
            value={customFolder}
            onChange={(e) => setCustomFolder(e.target.value)}
            placeholder="např. Marketing"
          />
        </Field>
      </div>
      <div className="mt-3">
        <Field label={cs.knowledge.title_}>
          <input
            className={inputClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="knowledge-new-title"
          />
        </Field>
      </div>
      <div className="mt-3">
        <Field label={cs.knowledge.readWhen}>
          <input
            className={inputClass}
            value={readWhen}
            onChange={(e) => setReadWhen(e.target.value)}
            placeholder={cs.knowledge.readWhenPlaceholder}
            data-testid="knowledge-new-read-when"
          />
        </Field>
        {/* prázdný spouštěč není detail formuláře: dokument pak vypadá
            založený, ale do rozcestníku workera se nikdy nedostane */}
        <div
          className="mt-1 text-[11px]"
          style={{
            color: readWhen.trim() ? "var(--text-tertiary)" : "var(--warning)",
          }}
        >
          {cs.knowledge.readWhenNote}
        </div>
      </div>
      <div className="mt-3">
        <Field label={cs.knowledge.content}>
          <textarea
            className={textareaClass}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            data-testid="knowledge-new-content"
          />
        </Field>
      </div>
      {error ? (
        <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Btn onClick={onCancel}>{cs.common.cancel}</Btn>
        <Btn
          variant="primary"
          disabled={title.trim().length < 2 || mutation.isPending}
          onClick={() => mutation.mutate()}
          data-testid="knowledge-new-save"
        >
          {cs.common.create}
        </Btn>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
